import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, Area, AreaChart, ReferenceDot } from "recharts";

function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function TideChart({ tide, moon }) {
  const { t } = useI18n();
  const series = (tide?.series || []).slice(0, 48); // first 24 hours
  const extremes = (tide?.extremes || []).filter((e) => {
    const ts = new Date(e.time).getTime();
    const first = series[0] && new Date(series[0].time).getTime();
    const last = series[series.length - 1] && new Date(series[series.length - 1].time).getTime();
    return first && last && ts >= first && ts <= last;
  });

  const data = series.map((p) => ({
    time: p.time,
    label: fmtTime(p.time),
    height: p.height,
  }));

  const todayExtremes = extremes.slice(0, 4);

  return (
    <div data-testid="tide-chart" className="clay p-6 md:p-7">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="eyebrow">{t("tides")} · 24h</p>
          <h3 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>{t("tides")}</h3>
          <p className="text-xs text-[color:var(--ink-2)] italic mt-1">{t("estimated_tides")}</p>
        </div>
        {moon && (
          <div className="text-right">
            <p className="eyebrow">{t("moon")}</p>
            <p className="font-semibold" style={{ fontFamily: "Outfit" }} data-testid="moon-phase-name">{moon.phase_name}</p>
            <p className="text-xs text-[color:var(--ink-2)]">{Math.round(moon.illumination * 100)}% {t("illumination")}</p>
          </div>
        )}
      </div>

      <div className="h-40 mt-3" style={{ minHeight: 160 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="tideFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1B3B6F" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#2A9D8F" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#5C757D" }} interval={Math.max(1, Math.floor(data.length / 6))} />
            <YAxis tick={{ fontSize: 10, fill: "#5C757D" }} width={28} domain={["auto", "auto"]} />
            <Tooltip
              contentStyle={{ borderRadius: 12, border: "1px solid #E3EBE8", fontSize: 12 }}
              formatter={(v) => [`${v} m`, t("tides")]}
            />
            <Area type="monotone" dataKey="height" stroke="#1B3B6F" strokeWidth={2} fill="url(#tideFill)" />
            {todayExtremes.map((e, i) => {
              const idx = data.findIndex((d) => d.time === e.time);
              if (idx < 0) return null;
              return (
                <ReferenceDot
                  key={i}
                  x={data[idx].label}
                  y={e.height}
                  r={5}
                  fill={e.type === "high" ? "#E76F51" : "#2A9D8F"}
                  stroke="#fff"
                  strokeWidth={2}
                  isFront
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div data-testid="tide-extremes" className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {todayExtremes.map((e, i) => (
          <div key={i} data-testid={`tide-extreme-${e.type}-${i}`} className="rounded-2xl bg-[color:var(--sand-2)] p-3">
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
              {e.type === "high" ? t("high") : t("low")}
            </p>
            <p className="font-semibold mt-0.5" style={{ fontFamily: "Outfit" }}>{fmtTime(e.time)}</p>
            <p className="text-xs text-[color:var(--ink-2)] mono">{e.height} m</p>
          </div>
        ))}
      </div>

      {moon?.next_full_moons?.length > 0 && (
        <div className="mt-5">
          <p className="eyebrow mb-2">{t("next_full_moons")}</p>
          <div className="flex flex-wrap gap-2">
            {moon.next_full_moons.map((m, i) => (
              <span
                key={i}
                data-testid={`full-moon-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--navy)]/8 text-[color:var(--navy)] px-3 py-1 text-xs font-medium"
              >
                <span className="w-2 h-2 rounded-full bg-[color:var(--navy)]" />
                {new Date(m.date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
